/**
 * collection-filter.js
 * Place in /assets/
 *
 * 3-level cascading category filter.
 * - L1 → navigates to /collections/{handle}
 * - L2 → navigates to /collections/{handle}  (or stays in page if same collection)
 * - L3 → filters via tag
 *
 * On page load, auto-activates the correct L1/L2/L3
 */

(function () {
  'use strict';

  class CollectionFilter {
    constructor(el) {
      this.el = el;

      const dataEl = document.getElementById('cf-data');
      if (!dataEl) return;
      this.tree = JSON.parse(dataEl.textContent);

      // Read current state from data attributes (set by Liquid)
      this.currentCollection = el.dataset.currentCollection || '';

      // DOM
      this.crumb    = el.querySelector('[data-cf-crumb]');
      this.col2     = document.getElementById('cf-col2');
      this.col3     = document.getElementById('cf-col3');
      this.subcats  = document.getElementById('cf-subcats');
      this.types    = document.getElementById('cf-types');

      this._bindL1();
      this._autoActivate();
    }

    // ── On page load: restore active state from URL ────────────

    _autoActivate() {
      if (!this.currentCollection) return;

      // Find which L1 key this collection belongs to
      let matchedL1Key = null;
      let matchedSub   = null;

      for (const [l1Key, l1Data] of Object.entries(this.tree)) {
        // Direct L1 match
        if (this.currentCollection === l1Key) {
          matchedL1Key = l1Key;
          break;
        }
        // L2 match — collection handle is one of the subs
        for (const sub of l1Data.subs) {
          if (sub.handle && this.currentCollection === sub.handle) {
            matchedL1Key = l1Key;
            matchedSub   = sub;
            break;
          }
          // Partial match: e.g. current = "indoor", sub.handle = "indoor"
          if (sub.handle && this.currentCollection.includes(sub.handle)) {
            matchedL1Key = l1Key;
            matchedSub   = sub;
          }
        }
        if (matchedL1Key) break;
      }

      if (!matchedL1Key) return;

      // Activate L1 link
      const l1Btn = this.el.querySelector(`[data-l1="${matchedL1Key}"]`);
      if (l1Btn) {
        this.el.querySelectorAll('[data-l1]').forEach(b => b.classList.remove('is-active'));
        l1Btn.classList.add('is-active');
      }

      this.activeL1 = {
        key:   matchedL1Key,
        label: l1Btn ? l1Btn.querySelector('.cf-item__label').textContent.trim() : matchedL1Key
      };

      // Render L2
      this._renderL2(matchedL1Key, matchedSub);

      // If a tag is active, find and activate the right L3
      if (this.currentTag && matchedSub) {
        const matchedChild = (matchedSub.children || []).find(
          c => c.tag === this.currentTag || this._handleize(c.label) === this.currentTag
        );
        if (matchedChild) {
          // renderL3 is called inside renderL2 when matchedSub has children
          // We just need to mark the right leaf active after render
          setTimeout(() => {
            const leaves = document.querySelectorAll('.cf-item--l3');
            leaves.forEach(l => {
              if (l.dataset.tag === this.currentTag) l.classList.add('is-active');
            });
            this.activeL3 = { label: matchedChild.label, tag: matchedChild.tag };
            this._updateCrumb();
          }, 0);
        }
      }

      this._updateCrumb();
    }

    // ── L1 ─────────────────────────────────────────────────────

    _bindL1() {
      this.el.querySelectorAll('[data-l1]').forEach(link => {
        link.addEventListener('click', (e) => {
          const key = link.dataset.l1;
          // Only intercept if this category has subs; otherwise let the link navigate
          if (this.tree[key] && this.tree[key].subs.length > 0) {
            // If already on this L1, just toggle sub panel without navigating
            if (this.currentCollection === key || (this.activeL1 && this.activeL1.key === key)) {
              e.preventDefault();
              this._selectL1(link, key);
            }
            // Otherwise let it navigate naturally (href on the <a> tag)
          }
        });
      });
    }

    _selectL1(el, key) {
      this.el.querySelectorAll('[data-l1]').forEach(b => b.classList.remove('is-active'));
      el.classList.add('is-active');
      this.activeL1 = { key, label: el.querySelector('.cf-item__label').textContent.trim() };
      this.activeL2 = null;
      this.activeL3 = null;
      this._renderL2(key, null);
      this._hideL3();
      this._updateCrumb();
    }

    // ── L2 ─────────────────────────────────────────────────────

    _renderL2(l1Key, preActiveSub) {
      const data = this.tree[l1Key];
      if (!data) return;

      this.subcats.innerHTML = '';

      data.subs.forEach(sub => {
        const li  = document.createElement('li');
        const a   = document.createElement('a');
        a.className = 'cf-item cf-item--l2';
        a.href = `/collections/${sub.handle}`;
        a.dataset.handle = sub.handle || '';

        let html = `<span class="cf-item__label">${sub.label}</span>`;
        if (sub.count) html += `<span class="cf-item__count">${sub.count}</span>`;
        if (sub.children && sub.children.length > 0) {
          html += `<svg class="cf-item__arrow" aria-hidden="true" width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M3.5 1.5L7 5L3.5 8.5" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>`;
        }
        a.innerHTML = html;

        // Pre-activate if this matches current collection
        if (preActiveSub && preActiveSub.handle === sub.handle) {
          a.classList.add('is-active');
          this.activeL2 = { label: sub.label, handle: sub.handle };
          if (sub.children && sub.children.length > 0) {
            this._renderL3(sub);
          }
        }

        a.addEventListener('click', (e) => {
          // If sub has children and we're already on this collection, show L3 without navigating
          if (sub.children && sub.children.length > 0 &&
              (this.currentCollection === sub.handle || this.currentCollection.includes(sub.handle))) {
            e.preventDefault();
          }
          this.subcats.querySelectorAll('.cf-item--l2').forEach(b => b.classList.remove('is-active'));
          a.classList.add('is-active');
          this.activeL2 = { label: sub.label, handle: sub.handle };
          this.activeL3 = null;
          if (sub.children && sub.children.length > 0) {
            this._renderL3(sub);
          } else {
            this._hideL3();
          }
          this._updateCrumb();
        });

        li.appendChild(a);
        this.subcats.appendChild(li);
      });

      this.col2.classList.remove('is-hidden');
    }

    // ── L3 ─────────────────────────────────────────────────────

    _renderL3(sub) {
      this.types.innerHTML = '';

      sub.children.forEach(child => {
        const li = document.createElement('li');
        const a  = document.createElement('a');
        a.className  = 'cf-item--l3';
        a.textContent = child.label;
        a.dataset.tag = child.tag || '';

        const baseUrl = `/collections/${sub.handle}`;
        a.href = child.tag ? `/collections/${sub.handle}/${child.tag}` : baseUrl;

        a.addEventListener('click', () => {
          this.types.querySelectorAll('.cf-item--l3').forEach(b => b.classList.remove('is-active'));
          a.classList.add('is-active');
          this.activeL3 = { label: child.label, tag: child.tag };
          this._updateCrumb();
          // Navigation happens naturally via href
        });

        li.appendChild(a);
        this.types.appendChild(li);
      });

      this.col3.classList.remove('is-hidden');
    }

    _hideL3() {
      this.col3.classList.add('is-hidden');
      this.types.innerHTML = '';
    }

    // ── Breadcrumb ─────────────────────────────────────────────

    _updateCrumb() {
      let html = '';
      if (this.activeL1) {
        html += `<span class="cf-crumb__item cf-crumb__item--active">${this.activeL1.label}</span>`;
      }
      if (this.activeL2) {
        html += `<span class="cf-crumb__sep">›</span>`;
        html += `<span class="cf-crumb__item${!this.activeL3 ? ' cf-crumb__item--active' : ''}">${this.activeL2.label}</span>`;
      }
      if (this.activeL3) {
        html += `<span class="cf-crumb__sep">›</span>`;
        html += `<span class="cf-crumb__item cf-crumb__item--active">${this.activeL3.label}</span>`;
      }

      this.crumb.innerHTML = html ||
        '<span class="cf-crumb__placeholder">The Collection</span>';
    }

    // ── Utility ────────────────────────────────────────────────

    _handleize(str) {
      return str.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    }
  }

  // ── Init ───────────────────────────────────────────────────

  function init() {
    document.querySelectorAll('[data-cf]').forEach(el => new CollectionFilter(el));
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init)
    : init();

})();
